export type CacheEntry<T> = {
  v: T;
  exp: number;
};

type ProductThumbValue = {
  src: string;
  updatedAt: number;
};

const NS = 'productThumbCache';
const now = () => Date.now();
const TTL_MS = 14 * 24 * 60 * 60 * 1000;

const toKey = (productId: string) => `${NS}:${productId}`;

function getItem<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.exp !== 'number') return null;
    if (parsed.exp < now()) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.v as T;
  } catch {
    return null;
  }
}

function setItem<T>(key: string, value: T, ttlMs: number) {
  try {
    const entry: CacheEntry<T> = { v: value, exp: now() + ttlMs };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
  }
}

function canStoreSrc(src: string) {
  const s = String(src || '').trim();
  if (!s) return false;
  if (s.length > 450_000) return false;
  return true;
}

export function getCachedProductThumbnailSrc(productId: string): string | null {
  const id = String(productId || '').trim();
  if (!id) return null;
  const v = getItem<ProductThumbValue>(toKey(id));
  if (!v?.src) return null;
  return v.src;
}

export async function cacheProductThumbnail(productId: string, imageUrl: string | null | undefined) {
  const id = String(productId || '').trim();
  const url = String(imageUrl || '').trim();
  if (!id || !url) return;

  const existing = getItem<ProductThumbValue>(toKey(id));
  if (existing?.updatedAt && existing.updatedAt > now() - 60_000) return;

  const store = (src: string) => {
    if (!canStoreSrc(src)) return;
    setItem<ProductThumbValue>(toKey(id), { src, updatedAt: now() }, TTL_MS);
  };

  if (url.startsWith('data:')) {
    store(url);
    return;
  }

  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const dataUrl: string = await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('timeout')), 3500);
      img.onload = () => {
        window.clearTimeout(timeout);
        try {
          const maxSize = 96;
          const w = (img as any).naturalWidth || img.width;
          const h = (img as any).naturalHeight || img.height;
          if (!w || !h) throw new Error('invalid image size');
          const scale = Math.min(maxSize / w, maxSize / h, 1);
          const tw = Math.max(1, Math.round(w * scale));
          const th = Math.max(1, Math.round(h * scale));
          const canvas = document.createElement('canvas');
          canvas.width = tw;
          canvas.height = th;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('no canvas context');
          ctx.drawImage(img, 0, 0, tw, th);
          const out = canvas.toDataURL('image/jpeg', 0.72);
          resolve(out);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('load error'));
      };
      img.src = url;
    });

    store(dataUrl);
  } catch {
    store(url);
  }
}

export function invalidateProductThumbnail(productId: string) {
  const id = String(productId || '').trim();
  if (!id) return;
  try {
    localStorage.removeItem(toKey(id));
  } catch {
  }
}
